import type { ReactNode } from 'react'
import type { ClinicIndex } from '@/lib/clinic/selectors'
import type {
    ClinicData,
    ClinicSettings,
    FollowUp,
    FollowUpInput,
    FollowUpStatus,
    Medicine,
    Patient,
    PatientInput,
    Prescription,
    PrescriptionInput,
    ReminderInput,
    ReminderLog,
    Visit,
    VisitSaveInput,
    VisitSaveResult
} from '@/lib/clinic/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { toast } from 'sonner'
import { createDefaultTreatments, createEmptyData } from '@/lib/clinic/defaults'
import { createSampleData } from '@/lib/clinic/seed'
import { buildIndex } from '@/lib/clinic/selectors'
import { localStorageRepository } from '@/lib/clinic/repository'
import { todayISO } from '@/lib/clinic/dates'

import {
    getPatientAge,
    normalizeText,
    patientCode,
    prescriptionCode,
    randomId
} from '@/lib/clinic/format'

const repository = localStorageRepository

export interface FollowUpSaveResult {
    followUp: FollowUp
    /** True when an existing pending follow-up was updated instead of creating a duplicate. */
    merged: boolean
}

export interface FollowUpStatusResult {
    ok: boolean
    conflict: FollowUp | null
}

export interface TreatmentSaveResult {
    ok: boolean
    error: string | null
}

interface ClinicContextValue {
    ready: boolean
    today: string
    data: ClinicData
    index: ClinicIndex
    createPatient: (input: PatientInput) => Patient
    updatePatient: (id: string, input: PatientInput) => Patient | null
    deletePatient: (id: string) => void
    saveVisit: (input: VisitSaveInput) => VisitSaveResult
    deleteVisit: (id: string) => void
    savePrescription: (input: PrescriptionInput, id?: string) => Prescription
    deletePrescription: (id: string) => void
    saveFollowUp: (input: FollowUpInput, id?: string) => FollowUpSaveResult
    setFollowUpStatus: (id: string, status: FollowUpStatus) => FollowUpStatusResult
    setFollowUpConfirmed: (id: string, confirmed: boolean) => void
    logReminder: (input: ReminderInput) => ReminderLog
    updateSettings: (updater: (settings: ClinicSettings) => ClinicSettings) => void
    saveTreatment: (name: string, id?: string) => TreatmentSaveResult
    deleteTreatment: (id: string) => void
    resetTreatments: () => void
    dismissSampleNotice: () => void
    loadSampleData: () => void
    clearPatientRecords: () => void
}

const ClinicContext = createContext<ClinicContextValue | null>(null)

const cleanPatient = (input: PatientInput): PatientInput => ({
    ...input,
    name: normalizeText(input.name),
    phone: input.phone.trim(),
    place: normalizeText(input.place),
    address: input.address.trim(),
    medicalHistory: input.medicalHistory.trim(),
    allergies: input.allergies.trim(),
    notes: input.notes.trim()
})

const cleanMedicines = (medicines: Medicine[]): Medicine[] => {
    return medicines
        .map(item => ({
            ...item,
            name: normalizeText(item.name),
            dosage: item.dosage.trim(),
            frequency: item.frequency.trim(),
            duration: item.duration.trim(),
            instructions: item.instructions.trim()
        }))
        .filter(item => item.name || item.dosage || item.frequency || item.duration || item.instructions)
}

export const ClinicProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [data, setData] = useState<ClinicData>(createEmptyData)
    const [ready, setReady] = useState(false)
    const [today, setToday] = useState('')
    const dataRef = useRef<ClinicData>(data)

    useEffect(() => {
        let cancelled = false

        const load = async (): Promise<void> => {
            const currentDay = todayISO()
            let loaded: ClinicData | null = null

            try {
                loaded = await repository.load()
            }
            catch {
                loaded = null
            }

            const next = loaded ?? createSampleData(currentDay)

            if (!loaded) {
                try {
                    await repository.save(next)
                }
                catch {
                    toast.error("Couldn't save the clinic records in this browser", {
                        description: "Changes will be lost when the page is closed. Check that the browser allows site data."
                    })
                }
            }

            if (cancelled) return

            dataRef.current = next
            setData(next)
            setToday(currentDay)
            setReady(true)
        }

        void load()

        const unsubscribe = repository.subscribe(() => {
            void repository.load().then(fresh => {
                if (!fresh || cancelled) return

                dataRef.current = fresh
                setData(fresh)
            })
        })

        const timer = window.setInterval(() => {
            const currentDay = todayISO()

            setToday(previous => (previous && previous !== currentDay ? currentDay : previous))
        }, 60000)

        return () => {
            cancelled = true
            unsubscribe()
            window.clearInterval(timer)
        }
    }, [])

    const commit = useCallback((next: ClinicData): void => {
        dataRef.current = next
        setData(next)

        repository.save(next).catch(() => {
            toast.error("Couldn't save the latest change", {
                description: "This browser's storage is full. Remove the clinic logo or older records, then try again."
            })
        })
    }, [])

    const createPatient = useCallback((input: PatientInput): Patient => {
        const current = dataRef.current
        const now = new Date().toISOString()
        const existingIds = new Set(current.patients.map(patient => patient.id))
        let number = current.counters.patient + 1

        while (existingIds.has(patientCode(number))) number += 1

        const cleaned = cleanPatient(input)
        const patient: Patient = {
            ...cleaned,
            id: patientCode(number),
            ageRecordedOn: cleaned.age === null ? null : todayISO(),
            createdAt: now,
            updatedAt: now
        }

        commit({
            ...current,
            patients: [...current.patients, patient],
            counters: { ...current.counters, patient: number }
        })

        return patient
    }, [commit])

    const updatePatient = useCallback((id: string, input: PatientInput): Patient | null => {
        const current = dataRef.current
        const existing = current.patients.find(patient => patient.id === id)

        if (!existing) return null

        const cleaned = cleanPatient(input)
        const day = todayISO()
        const currentAge = existing.dateOfBirth ? null : getPatientAge(existing, day)
        const keepAge = cleaned.age !== null && cleaned.age === currentAge
        const updated: Patient = {
            ...existing,
            ...cleaned,
            age: keepAge ? existing.age : cleaned.age,
            ageRecordedOn: cleaned.age === null ? null : keepAge ? existing.ageRecordedOn : day,
            updatedAt: new Date().toISOString()
        }

        commit({ ...current, patients: current.patients.map(patient => (patient.id === id ? updated : patient)) })

        return updated
    }, [commit])

    const deletePatient = useCallback((id: string): void => {
        const current = dataRef.current

        commit({
            ...current,
            patients: current.patients.filter(patient => patient.id !== id),
            visits: current.visits.filter(visit => visit.patientId !== id),
            prescriptions: current.prescriptions.filter(prescription => prescription.patientId !== id),
            followUps: current.followUps.filter(followUp => followUp.patientId !== id),
            reminders: current.reminders.filter(reminder => reminder.patientId !== id)
        })
    }, [commit])

    const saveVisit = useCallback((input: VisitSaveInput): VisitSaveResult => {
        const current = dataRef.current
        const now = new Date().toISOString()
        const existing = input.id ? current.visits.find(visit => visit.id === input.id) : undefined

        const visit: Visit = {
            id: existing?.id ?? randomId('vis'),
            patientId: input.patientId,
            date: input.date,
            complaint: normalizeText(input.complaint),
            diagnosis: input.diagnosis.trim(),
            treatment: input.treatment.trim(),
            toothNumbers: normalizeText(input.toothNumbers),
            notes: input.notes.trim(),
            createdAt: existing?.createdAt ?? now,
            updatedAt: now
        }

        const visits = existing
            ? current.visits.map(item => (item.id === visit.id ? visit : item))
            : [...current.visits, visit]

        let followUps = current.followUps
        let completedFollowUp: FollowUp | null = null

        const toComplete = input.completeFollowUpId
            ? followUps.find(item => item.id === input.completeFollowUpId && item.status === 'pending')
            : undefined

        if (toComplete) {
            const completed: FollowUp = {
                ...toComplete,
                status: 'completed',
                completedAt: now,
                updatedAt: now
            }

            completedFollowUp = completed
            followUps = followUps.map(item => (item.id === completed.id ? completed : item))
        }

        const patientPending = followUps
            .filter(item => item.patientId === visit.patientId && item.status === 'pending')
            .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
        const linked = patientPending.find(item => item.visitId === visit.id) ?? null

        let followUp: FollowUp | null = null
        let followUpChange: VisitSaveResult['followUpChange'] = null

        if (input.nextVisit) {
            const next = input.nextVisit
            const target = linked ?? (existing ? null : patientPending[0] ?? null)

            if (target) {
                const moved = target.dueDate !== next.date || (target.dueTime ?? null) !== (next.time ?? null)
                const updated: FollowUp = {
                    ...target,
                    visitId: visit.id,
                    dueDate: next.date,
                    dueTime: next.time,
                    reason: normalizeText(next.reason) || target.reason,
                    confirmedAt: moved ? null : target.confirmedAt,
                    updatedAt: now
                }

                followUp = updated
                followUpChange = 'updated'
                followUps = followUps.map(item => (item.id === updated.id ? updated : item))
            }
            else if (!existing || patientPending.length === 0) {
                const created: FollowUp = {
                    id: randomId('fu'),
                    patientId: visit.patientId,
                    visitId: visit.id,
                    dueDate: next.date,
                    dueTime: next.time,
                    reason: normalizeText(next.reason) || visit.treatment.split('\n')[0] || visit.complaint,
                    notes: '',
                    status: 'pending',
                    confirmedAt: null,
                    completedAt: null,
                    cancelledAt: null,
                    createdAt: now,
                    updatedAt: now
                }

                followUp = created
                followUpChange = 'created'
                followUps = [...followUps, created]
            }
        }
        else if (linked && existing) {
            const cancelled: FollowUp = {
                ...linked,
                status: 'cancelled',
                cancelledAt: now,
                notes: linked.notes || 'Returning date removed from the visit record.',
                updatedAt: now
            }

            followUp = cancelled
            followUpChange = 'cancelled'
            followUps = followUps.map(item => (item.id === cancelled.id ? cancelled : item))
        }

        commit({ ...current, visits, followUps })

        return { visit, followUp, followUpChange, completedFollowUp }
    }, [commit])

    const deleteVisit = useCallback((id: string): void => {
        const current = dataRef.current
        const removedFollowUps = new Set(
            current.followUps.filter(item => item.visitId === id && item.status === 'pending').map(item => item.id)
        )

        commit({
            ...current,
            visits: current.visits.filter(visit => visit.id !== id),
            prescriptions: current.prescriptions.map(item => (item.visitId === id ? { ...item, visitId: null } : item)),
            followUps: current.followUps
                .filter(item => !removedFollowUps.has(item.id))
                .map(item => (item.visitId === id ? { ...item, visitId: null } : item)),
            reminders: current.reminders.filter(item => !item.followUpId || !removedFollowUps.has(item.followUpId))
        })
    }, [commit])

    const savePrescription = useCallback((input: PrescriptionInput, id?: string): Prescription => {
        const current = dataRef.current
        const now = new Date().toISOString()
        const cleaned: PrescriptionInput = {
            ...input,
            complaint: input.complaint.trim(),
            diagnosis: input.diagnosis.trim(),
            advice: input.advice.trim(),
            instructions: input.instructions.trim(),
            notes: input.notes.trim(),
            medicines: cleanMedicines(input.medicines)
        }

        const existing = id ? current.prescriptions.find(item => item.id === id) : undefined

        if (existing) {
            const updated: Prescription = { ...existing, ...cleaned, id: existing.id, createdAt: existing.createdAt, updatedAt: now }

            commit({ ...current, prescriptions: current.prescriptions.map(item => (item.id === existing.id ? updated : item)) })

            return updated
        }

        const existingIds = new Set(current.prescriptions.map(item => item.id))
        let number = current.counters.prescription + 1

        while (existingIds.has(prescriptionCode(number))) number += 1

        const created: Prescription = { ...cleaned, id: prescriptionCode(number), createdAt: now, updatedAt: now }

        commit({
            ...current,
            prescriptions: [...current.prescriptions, created],
            counters: { ...current.counters, prescription: number }
        })

        return created
    }, [commit])

    const deletePrescription = useCallback((id: string): void => {
        const current = dataRef.current

        commit({ ...current, prescriptions: current.prescriptions.filter(item => item.id !== id) })
    }, [commit])

    const saveFollowUp = useCallback((input: FollowUpInput, id?: string): FollowUpSaveResult => {
        const current = dataRef.current
        const now = new Date().toISOString()
        const target = id
            ? current.followUps.find(item => item.id === id)
            : current.followUps.find(item => item.patientId === input.patientId && item.status === 'pending')

        if (target) {
            const updated: FollowUp = {
                ...target,
                visitId: input.visitId ?? target.visitId,
                dueDate: input.dueDate,
                dueTime: input.dueTime,
                reason: normalizeText(input.reason),
                notes: input.notes.trim(),
                confirmedAt: input.confirmed ? target.confirmedAt ?? now : null,
                updatedAt: now
            }

            commit({ ...current, followUps: current.followUps.map(item => (item.id === updated.id ? updated : item)) })

            return { followUp: updated, merged: !id }
        }

        const created: FollowUp = {
            id: randomId('fu'),
            patientId: input.patientId,
            visitId: input.visitId,
            dueDate: input.dueDate,
            dueTime: input.dueTime,
            reason: normalizeText(input.reason),
            notes: input.notes.trim(),
            status: 'pending',
            confirmedAt: input.confirmed ? now : null,
            completedAt: null,
            cancelledAt: null,
            createdAt: now,
            updatedAt: now
        }

        commit({ ...current, followUps: [...current.followUps, created] })

        return { followUp: created, merged: false }
    }, [commit])

    const setFollowUpStatus = useCallback((id: string, status: FollowUpStatus): FollowUpStatusResult => {
        const current = dataRef.current
        const target = current.followUps.find(item => item.id === id)

        if (!target) return { ok: false, conflict: null }

        if (status === 'pending') {
            const conflict = current.followUps.find(item => item.id !== id && item.patientId === target.patientId && item.status === 'pending')

            if (conflict) return { ok: false, conflict }
        }

        const now = new Date().toISOString()
        const updated: FollowUp = {
            ...target,
            status,
            completedAt: status === 'completed' ? now : null,
            cancelledAt: status === 'cancelled' ? now : null,
            updatedAt: now
        }

        commit({ ...current, followUps: current.followUps.map(item => (item.id === id ? updated : item)) })

        return { ok: true, conflict: null }
    }, [commit])

    const setFollowUpConfirmed = useCallback((id: string, confirmed: boolean): void => {
        const current = dataRef.current
        const now = new Date().toISOString()

        commit({
            ...current,
            followUps: current.followUps.map(item => (
                item.id === id ? { ...item, confirmedAt: confirmed ? item.confirmedAt ?? now : null, updatedAt: now } : item
            ))
        })
    }, [commit])

    const logReminder = useCallback((input: ReminderInput): ReminderLog => {
        const current = dataRef.current
        const now = new Date().toISOString()
        const reminder: ReminderLog = { ...input, note: input.note.trim(), id: randomId('rem'), createdAt: now }

        const followUps = input.outcome === 'confirmed' && input.followUpId
            ? current.followUps.map(item => (
                item.id === input.followUpId ? { ...item, confirmedAt: item.confirmedAt ?? now, updatedAt: now } : item
            ))
            : current.followUps

        commit({ ...current, reminders: [...current.reminders, reminder], followUps })

        return reminder
    }, [commit])

    const updateSettings = useCallback((updater: (settings: ClinicSettings) => ClinicSettings): void => {
        const current = dataRef.current

        commit({ ...current, settings: updater(current.settings) })
    }, [commit])

    const saveTreatment = useCallback((name: string, id?: string): TreatmentSaveResult => {
        const current = dataRef.current
        const cleaned = normalizeText(name)

        if (!cleaned) return { ok: false, error: "Enter a name." }

        const duplicate = current.treatments.find(item => item.id !== id && item.name.toLowerCase() === cleaned.toLowerCase())

        if (duplicate) return { ok: false, error: `"${duplicate.name}" is already in the list.` }

        const treatments = id
            ? current.treatments.map(item => (item.id === id ? { ...item, name: cleaned } : item))
            : [...current.treatments, { id: randomId('tr'), name: cleaned }]

        commit({ ...current, treatments })

        return { ok: true, error: null }
    }, [commit])

    const deleteTreatment = useCallback((id: string): void => {
        const current = dataRef.current

        commit({ ...current, treatments: current.treatments.filter(item => item.id !== id) })
    }, [commit])

    const resetTreatments = useCallback((): void => {
        const current = dataRef.current

        commit({ ...current, treatments: createDefaultTreatments() })
    }, [commit])

    const dismissSampleNotice = useCallback((): void => {
        const current = dataRef.current

        commit({ ...current, meta: { ...current.meta, sampleNoticeDismissed: true } })
    }, [commit])

    const loadSampleData = useCallback((): void => {
        const current = dataRef.current
        const sample = createSampleData(todayISO())

        commit({ ...sample, settings: current.settings, treatments: current.treatments })
    }, [commit])

    const clearPatientRecords = useCallback((): void => {
        const current = dataRef.current

        commit({
            ...current,
            patients: [],
            visits: [],
            prescriptions: [],
            followUps: [],
            reminders: [],
            counters: { patient: 1000, prescription: 1000 },
            meta: { ...current.meta, sample: false, sampleNoticeDismissed: true }
        })
    }, [commit])

    const index = useMemo(() => buildIndex(data), [data])

    const value = useMemo<ClinicContextValue>(() => ({
        ready,
        today,
        data,
        index,
        createPatient,
        updatePatient,
        deletePatient,
        saveVisit,
        deleteVisit,
        savePrescription,
        deletePrescription,
        saveFollowUp,
        setFollowUpStatus,
        setFollowUpConfirmed,
        logReminder,
        updateSettings,
        saveTreatment,
        deleteTreatment,
        resetTreatments,
        dismissSampleNotice,
        loadSampleData,
        clearPatientRecords
    }), [
        ready,
        today,
        data,
        index,
        createPatient,
        updatePatient,
        deletePatient,
        saveVisit,
        deleteVisit,
        savePrescription,
        deletePrescription,
        saveFollowUp,
        setFollowUpStatus,
        setFollowUpConfirmed,
        logReminder,
        updateSettings,
        saveTreatment,
        deleteTreatment,
        resetTreatments,
        dismissSampleNotice,
        loadSampleData,
        clearPatientRecords
    ])

    return (
        <ClinicContext.Provider value={value}>
            {children}
        </ClinicContext.Provider>
    )
}

export const useClinic = (): ClinicContextValue => {
    const context = useContext(ClinicContext)

    if (!context) throw new Error('useClinic must be used inside ClinicProvider')

    return context
}
